import React, { useState } from 'react';
import {
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   Button,
   Box,
   Typography,
   Card,
   CardContent,
   IconButton,
   TextField,
   FormControlLabel,
   Checkbox,
   Stepper,
   Step,
   StepLabel,
   Avatar,
   Chip,
   Alert,
   CircularProgress,
   Grid,
   FormControl,
   InputLabel,
   Select,
   MenuItem,
   FormHelperText,
} from '@mui/material';
import {
   Close as CloseIcon,
   Lock as LockIcon,
   Public as PublicIcon,
   AttachMoney as MoneyIcon,
   Check as CheckIcon,
   ArrowBack as ArrowBackIcon,
   Key as KeyIcon,
   Link as LinkIcon,
   People as PeopleIcon,
   VpnKey as VpnKeyIcon,
   Refresh as RefreshIcon,
} from '@mui/icons-material';

interface CreateAlbumDialogProps {
   open: boolean;
   onClose: () => void;
   onCreateAlbum: (albumData: {
      title: string;
      description?: string;
      type: 'private' | 'public' | 'paid';
      enableParticipantParticipation: boolean;
      pricePerPhoto?: number;
      pricingModel?: 'per_photo' | 'one_time';
      albumDate?: string;
   }) => Promise<void>;
}

type AlbumType = 'private' | 'public' | 'paid';

const CreateAlbumDialog: React.FC<CreateAlbumDialogProps> = ({ open, onClose, onCreateAlbum }) => {
   const [activeStep, setActiveStep] = useState(0);
   const [selectedType, setSelectedType] = useState<AlbumType | null>(null);
   const [title, setTitle] = useState('');
   const [description, setDescription] = useState('');
   const [albumDate, setAlbumDate] = useState('');
   const [enableParticipantParticipation, setEnableParticipantParticipation] = useState(true);
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState('');
   const [showTitleError, setShowTitleError] = useState(false);

   // Step 3 state variables
   const [pricePerPhoto, setPricePerPhoto] = useState('5.00');
   const [pricingModel, setPricingModel] = useState<'per_photo' | 'one_time'>('per_photo');
   const [paidAlbumAccessType, setPaidAlbumAccessType] = useState<'public' | 'private'>('public');

   const steps = ['Choose Type', 'Album Details', 'Create Album'];

   const albumTypes = [
      {
         id: 'private' as AlbumType,
         title: 'Private Album',
         description:
            'Invite-only with participant approval. Perfect for personal events and private gatherings.',
         icon: <LockIcon sx={{ fontSize: 40, color: '#6366f1' }} />,
         features: ['QR code access', 'Participant approvals', 'Controlled sharing'],
         disabled: false,
      },
      {
         id: 'public' as AlbumType,
         title: 'Public Album',
         description:
            'Anyone with the access code can view and upload photos. Great for open events.',
         icon: <PublicIcon sx={{ fontSize: 40, color: '#10b981' }} />,
         features: ['Public link sharing', 'Anyone can upload', 'All contributors displayed'],
         disabled: false,
      },
      {
         id: 'paid' as AlbumType,
         title: 'Paid Album',
         description:
            'Photographer sets price per photo. Photos shown with watermark until purchased.',
         icon: <MoneyIcon sx={{ fontSize: 40, color: '#f59e0b' }} />,
         features: ['Set price per photo', 'Watermarked previews', 'Download after purchase'],
         disabled: false,
      },
   ];

   // Access code and share link generation removed

   const handleClose = () => {
      setActiveStep(0);
      setSelectedType(null);
      setTitle('');
      setDescription('');
      setAlbumDate('');
      setEnableParticipantParticipation(true);
      setError('');
      setShowTitleError(false);
      setPricePerPhoto('5.00');
      setPricingModel('per_photo');
      onClose();
   };

   const handleNext = () => {
      if (activeStep === 0 && !selectedType) {
         setError('Please select an album type');
         return;
      }
      if (activeStep === 1 && !title.trim()) {
         setError('Album title is required');
         setShowTitleError(true);
         return;
      }
      if (activeStep === 1 && selectedType === 'public') {
         // Skip step 3 for public albums, go directly to create
         setActiveStep(3);
         return;
      }
      // Step 3 logic removed for public albums
      setError('');
      setShowTitleError(false);
      setActiveStep(activeStep + 1);
   };

   const handleBack = () => {
      setError('');
      setActiveStep(activeStep - 1);
   };

   const handleCreateAlbum = async () => {
      if (!title.trim()) {
         setError('Album title is required');
         return;
      }

      setLoading(true);
      setError('');

      try {
         // Simple album data - only essential fields
         const albumData = {
            title: title.trim(),
            description: description.trim() || '',
            type: selectedType!,
            enableParticipantParticipation,
            albumDate: albumDate || undefined,
            ...(selectedType === 'paid' && {
               pricePerPhoto: parseFloat(pricePerPhoto) || 5.0,
               pricingModel,
               paidAlbumAccessType, // public or private
            }),
         };

         console.log('📝 CreateAlbumDialog - Album data:', albumData);
         await onCreateAlbum(albumData);
         handleClose();
      } catch (err) {
         setError(err instanceof Error ? err.message : 'Failed to create album');
      } finally {
         setLoading(false);
      }
   };

   const renderStepContent = () => {
      if (activeStep === 0) {
         return (
            <Box>
               <Typography variant='h6' sx={{ mb: 1, textAlign: 'center', fontWeight: 600 }}>
                  Choose Album Type
               </Typography>
               <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ mb: 3, textAlign: 'center' }}
               >
                  Select the type of album that best fits your needs.
               </Typography>

               <Box
                  sx={{
                     display: 'flex',
                     flexDirection: { xs: 'column', sm: 'row' },
                     gap: 2,
                     justifyContent: 'center',
                     alignItems: 'center',
                  }}
               >
                  {albumTypes.map((type) => (
                     <Card
                        key={type.id}
                        sx={{
                           cursor: type.disabled ? 'not-allowed' : 'pointer',
                           border:
                              selectedType === type.id ? '2px solid #01949A' : '1px solid #e5e7eb',
                           borderRadius: 2,
                           minWidth: { xs: '100%', sm: 200 },
                           maxWidth: { xs: '100%', sm: 250 },
                           width: { xs: '100%', sm: 'auto' },
                           opacity: type.disabled ? 0.5 : 1,
                           '&:hover': {
                              borderColor: type.disabled
                                 ? '#e5e7eb'
                                 : selectedType === type.id
                                   ? '#01949A'
                                   : '#9ca3af',
                           },
                           transition: 'all 0.2s ease-in-out',
                           backgroundColor: type.disabled
                              ? '#f3f4f6'
                              : selectedType === type.id
                                ? 'rgba(1, 148, 154, 0.1)'
                                : '#F5F1E8',
                        }}
                        onClick={() => !type.disabled && setSelectedType(type.id)}
                     >
                        <CardContent sx={{ p: 3, textAlign: 'center', position: 'relative' }}>
                           {type.disabled && (
                              <Box
                                 sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    backgroundColor: '#6b7280',
                                    color: 'white',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                 }}
                              >
                                 Coming Soon
                              </Box>
                           )}
                           {selectedType === type.id && !type.disabled && (
                              <CheckIcon
                                 sx={{
                                    color: '#01949A',
                                    fontSize: 20,
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                 }}
                              />
                           )}
                           <Box
                              sx={{
                                 display: 'flex',
                                 flexDirection: 'column',
                                 alignItems: 'center',
                                 gap: 2,
                              }}
                           >
                              <Box
                                 sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 48,
                                    height: 48,
                                    backgroundColor: '#374151',
                                    borderRadius: '50%',
                                    flexShrink: 0,
                                 }}
                              >
                                 {React.cloneElement(type.icon, {
                                    sx: { color: 'white', fontSize: 24 },
                                 })}
                              </Box>
                              <Box sx={{ width: '100%' }}>
                                 <Typography
                                    variant='h6'
                                    sx={{
                                       mb: 1,
                                       fontWeight: 600,
                                       color: '#1f2937',
                                       fontSize: '1rem',
                                    }}
                                 >
                                    {type.title}
                                 </Typography>
                                 <Typography
                                    variant='body2'
                                    color='text.secondary'
                                    sx={{ mb: 2, lineHeight: 1.4, fontSize: '0.875rem' }}
                                 >
                                    {type.description}
                                 </Typography>
                                 <Box
                                    sx={{
                                       display: 'flex',
                                       flexDirection: 'column',
                                       gap: 0.5,
                                       alignItems: 'flex-start',
                                    }}
                                 >
                                    {type.features.map((feature) => (
                                       <Box
                                          key={feature}
                                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                       >
                                          <Box
                                             sx={{
                                                width: 4,
                                                height: 4,
                                                borderRadius: '50%',
                                                backgroundColor: '#01949A',
                                             }}
                                          />
                                          <Typography
                                             variant='body2'
                                             sx={{ fontSize: '0.8rem', color: '#6b7280' }}
                                          >
                                             {feature}
                                          </Typography>
                                       </Box>
                                    ))}
                                 </Box>
                              </Box>
                           </Box>
                        </CardContent>
                     </Card>
                  ))}
               </Box>
            </Box>
         );
      }

      if (activeStep === 1) {
         return (
            <Box>
               <Typography variant='h6' sx={{ mb: 1, textAlign: 'center', fontWeight: 600 }}>
                  Album Details
               </Typography>
               <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ mb: 3, textAlign: 'center' }}
               >
                  Provide basic information about your album.
               </Typography>

               <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <TextField
                     label='Album Title *'
                     value={title}
                     onChange={(e) => {
                        setTitle(e.target.value);
                        if (showTitleError && e.target.value.trim()) {
                           setShowTitleError(false);
                        }
                     }}
                     fullWidth
                     required
                     placeholder='e.g., Summer Wedding 2025'
                     error={showTitleError}
                     helperText={showTitleError ? 'Album title is required' : ''}
                  />

                  <TextField
                     label='Description'
                     value={description}
                     onChange={(e) => setDescription(e.target.value)}
                     fullWidth
                     multiline
                     rows={4}
                     placeholder='Add a description for your album...'
                  />

                  <TextField
                     label='Album Date'
                     type='date'
                     value={albumDate}
                     onChange={(e) => setAlbumDate(e.target.value)}
                     fullWidth
                     InputLabelProps={{
                        shrink: true,
                     }}
                  />
               </Box>
            </Box>
         );
      }

      if (activeStep === 2) {
         if (selectedType === 'public') {
            return (
               <Box>
                  <Typography variant='h6' sx={{ mb: 1, textAlign: 'center', fontWeight: 600 }}>
                     Public Album Ready
                  </Typography>
                  <Typography
                     variant='body2'
                     color='text.secondary'
                     sx={{ mb: 3, textAlign: 'center' }}
                  >
                     Your public album will be accessible to anyone with the link.
                  </Typography>

                  <Card
                     sx={{
                        p: 3,
                        backgroundColor: 'rgba(1, 148, 154, 0.1)',
                        border: '1px solid #01949A',
                        borderRadius: 2,
                     }}
                  >
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <PublicIcon sx={{ color: '#01949A', fontSize: 24 }} />
                        <Box>
                           <Typography
                              variant='subtitle1'
                              sx={{ fontWeight: 600, fontSize: '1rem' }}
                           >
                              Public Album
                           </Typography>
                           <Typography
                              variant='body2'
                              color='text.secondary'
                              sx={{ fontSize: '0.875rem' }}
                           >
                              Anyone with the link can view and upload photos to your album
                           </Typography>
                        </Box>
                     </Box>
                  </Card>
               </Box>
            );
         }

         if (selectedType === 'private') {
            return (
               <Box>
                  <Typography variant='h6' sx={{ mb: 1, textAlign: 'center', fontWeight: 600 }}>
                     Private Album Settings
                  </Typography>
                  <Typography
                     variant='body2'
                     color='text.secondary'
                     sx={{ mb: 3, textAlign: 'center' }}
                  >
                     You can add participants after creating the album.
                  </Typography>

                  <Card
                     sx={{
                        p: 3,
                        backgroundColor: 'rgba(1, 148, 154, 0.1)',
                        border: '1px solid #01949A',
                        borderRadius: 2,
                     }}
                  >
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <PeopleIcon sx={{ color: '#01949A', fontSize: 24 }} />
                        <Typography variant='subtitle1' sx={{ fontWeight: 600, fontSize: '1rem' }}>
                           Participant Management
                        </Typography>
                     </Box>
                     <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ mb: 2, fontSize: '0.875rem' }}
                     >
                        After creating your album, you'll be able to:
                     </Typography>
                     <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {[
                           'Invite participants via email or SMS',
                           'Require approval for new members',
                           'Generate QR codes for easy access',
                           'Control who can view and upload photos',
                        ].map((feature) => (
                           <Box
                              key={feature}
                              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                           >
                              <Box
                                 sx={{
                                    width: 4,
                                    height: 4,
                                    borderRadius: '50%',
                                    backgroundColor: '#01949A',
                                 }}
                              />
                              <Typography variant='body2' sx={{ fontSize: '0.875rem' }}>
                                 {feature}
                              </Typography>
                           </Box>
                        ))}
                     </Box>
                  </Card>
               </Box>
            );
         }

         if (selectedType === 'paid') {
            return (
               <Box>
                  <Typography variant='h6' sx={{ mb: 1, textAlign: 'center', fontWeight: 600 }}>
                     Pricing Configuration
                  </Typography>
                  <Typography
                     variant='body2'
                     color='text.secondary'
                     sx={{ mb: 3, textAlign: 'center' }}
                  >
                     Choose your pricing model and set the price.
                  </Typography>

                  {/* Access Type Dropdown */}
                  <FormControl fullWidth sx={{ mb: 3 }}>
                     <InputLabel id='access-type-label'>Album Access Type</InputLabel>
                     <Select
                        labelId='access-type-label'
                        value={paidAlbumAccessType}
                        label='Album Access Type'
                        onChange={(e) => setPaidAlbumAccessType(e.target.value as 'public' | 'private')}
                     >
                        <MenuItem value='public'>
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PublicIcon sx={{ fontSize: 20, color: '#6b7280' }} />
                              <Box>
                                 <Typography variant='body2' sx={{ fontWeight: 600 }}>
                                    Public Access
                                 </Typography>
                                 <Typography variant='caption' color='text.secondary'>
                                    Anyone with the link can pay and access
                                 </Typography>
                              </Box>
                           </Box>
                        </MenuItem>
                        <MenuItem value='private'>
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LockIcon sx={{ fontSize: 20, color: '#6b7280' }} />
                              <Box>
                                 <Typography variant='body2' sx={{ fontWeight: 600 }}>
                                    Private Access
                                 </Typography>
                                 <Typography variant='caption' color='text.secondary'>
                                    Only invited users can pay and access
                                 </Typography>
                              </Box>
                           </Box>
                        </MenuItem>
                     </Select>
                     <FormHelperText>
                        {paidAlbumAccessType === 'public' 
                           ? 'Anyone with the link can pay and access'
                           : 'Only invited users can pay and access'}
                     </FormHelperText>
                  </FormControl>

                  {/* Pricing Model Dropdown */}
                  <FormControl fullWidth sx={{ mb: 3 }}>
                     <InputLabel id='pricing-model-label'>Pricing Model</InputLabel>
                     <Select
                        labelId='pricing-model-label'
                        value={pricingModel}
                        label='Pricing Model'
                        onChange={(e) => setPricingModel(e.target.value as 'per_photo' | 'one_time')}
                     >
                        <MenuItem value='per_photo'>
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <MoneyIcon sx={{ fontSize: 20, color: '#6b7280' }} />
                              <Box>
                                 <Typography variant='body2' sx={{ fontWeight: 600 }}>
                                    Per Photo
                                 </Typography>
                                 <Typography variant='caption' color='text.secondary'>
                                    Users pay for each photo they download
                                 </Typography>
                              </Box>
                           </Box>
                        </MenuItem>
                        <MenuItem value='one_time'>
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LockIcon sx={{ fontSize: 20, color: '#6b7280' }} />
                              <Box>
                                 <Typography variant='body2' sx={{ fontWeight: 600 }}>
                                    One-Time Fee
                                 </Typography>
                                 <Typography variant='caption' color='text.secondary'>
                                    Pay once to access all photos
                                 </Typography>
                              </Box>
                           </Box>
                        </MenuItem>
                     </Select>
                     <FormHelperText>
                        {pricingModel === 'per_photo' 
                           ? 'Users pay for each photo they download'
                           : 'Pay once to access all photos'}
                     </FormHelperText>
                  </FormControl>

                  {/* Price Input */}
                  <Box sx={{ mb: 3 }}>
                     <Typography variant='subtitle2' sx={{ mb: 1, fontWeight: 600 }}>
                        {pricingModel === 'per_photo' ? 'Price Per Photo' : 'Album Access Fee'}
                     </Typography>
                     <Typography variant='body2' color='text.secondary' sx={{ mb: 2, fontSize: '0.875rem' }}>
                        {pricingModel === 'per_photo' 
                           ? 'Set the price that users will pay to download each photo without watermark.'
                           : 'Set the one-time fee for unlimited access to all photos in the album.'}
                     </Typography>

                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant='h6' sx={{ fontSize: '1.25rem' }}>
                           $
                        </Typography>
                        <TextField
                           value={pricePerPhoto}
                           onChange={(e) => setPricePerPhoto(e.target.value)}
                           sx={{ width: 120 }}
                           size="small"
                        />
                        <Typography variant='body2' color='text.secondary'>
                           USD
                        </Typography>
                     </Box>
                  </Box>
               </Box>
            );
         }
      }

      return null;
   };

   return (
      <Dialog
         open={open}
         onClose={handleClose}
         maxWidth='md'
         fullWidth
         PaperProps={{
            sx: {
               borderRadius: 2,
               maxWidth: { xs: '95vw', sm: '700px', md: '800px' },
               margin: 'auto',
               minHeight: '500px',
               backgroundColor: '#F5F1E8',
            },
         }}
      >
         <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {activeStep > 0 && (
                     <IconButton onClick={handleBack} size='small' sx={{ mr: 1 }}>
                        <ArrowBackIcon />
                     </IconButton>
                  )}
                  <Box>
                     <Typography variant='h6' sx={{ fontWeight: 600 }}>
                        Create Album
                     </Typography>
                     <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
                        {selectedType === 'public' && activeStep === 1
                           ? 'Step 2 of 2 • Basic Info'
                           : `Step ${activeStep + 1} of 3 • ${
                                activeStep === 0
                                   ? 'Choose Type'
                                   : activeStep === 1
                                     ? 'Basic Info'
                                     : 'Configure'
                             }`}
                     </Typography>
                  </Box>
               </Box>
               <IconButton onClick={handleClose} size='small'>
                  <CloseIcon />
               </IconButton>
            </Box>
         </DialogTitle>

         <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
            {error && (
               <Alert severity='error' sx={{ mb: 2 }}>
                  {error}
               </Alert>
            )}

            {renderStepContent()}
         </DialogContent>

         <DialogActions
            sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 }, gap: 2, justifyContent: 'flex-end' }}
         >
            <Button
               onClick={activeStep === 0 ? handleClose : handleBack}
               sx={{
                  color: '#6b7280',
                  textTransform: 'none',
                  fontWeight: 500,
                  '&:hover': {
                     backgroundColor: 'rgba(1, 148, 154, 0.1)',
                     color: '#01949A',
                  },
               }}
            >
               {activeStep === 0 ? 'Cancel' : 'Back'}
            </Button>
            <Button
               variant='contained'
               onClick={
                  selectedType === 'public' && activeStep === 1
                     ? handleCreateAlbum
                     : activeStep < 2
                       ? handleNext
                       : handleCreateAlbum
               }
               disabled={loading}
               startIcon={loading ? <CircularProgress size={20} /> : null}
               sx={{
                  bgcolor: '#01949A',
                  color: 'white',
                  fontWeight: 600,
                  px: 3,
                  textTransform: 'none',
                  '&:hover': {
                     bgcolor: '#007075',
                  },
                  '&:disabled': {
                     bgcolor: '#d1d5db',
                     color: '#9ca3af',
                  },
               }}
            >
               {loading
                  ? 'Creating...'
                  : selectedType === 'public' && activeStep === 1
                    ? 'Create Album'
                    : activeStep < 2
                      ? 'Next'
                      : 'Create Album'}
            </Button>
         </DialogActions>
      </Dialog>
   );
};

export default CreateAlbumDialog;
